/* eslint-disable  react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Dialog, DialogTitle, Divider, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import { Button } from "components/Forms/StyledComponents";
import ButtonWithLoading from "components/ButtonWithLoading";
import openAuthPopup, {
  isClosedByUser,
} from "pages/Integrations/Accounts/openAuthPopup";
import { ContentLoader, BreadcrumbsLoader } from "./Loaders";
import FolderList from "./FolderList";
import SortMenu from "./SortMenu";
import Breadcrumbs from "./Breadcrumbs";
import {
  CloseButton,
  EmptyFolderContainer,
  NavigationContainer,
  StyledDialogActions,
} from "./Styled";
import { fetchFolderChildren, fetchPickerContent } from "./api";
import { StorageFolder, StorageProviderWithAuth } from "@dashboard-v3/api";
import { PickerState, RequestParams, SortConfiguration } from "./types";

interface Props {
  provider: StorageProviderWithAuth;
  isOpen: boolean;
  multi?: boolean;
  onClose: () => void;
  onSelect: (folders: StorageFolder[]) => void;
}

export enum LoadingElement {
  LIST = "LIST",
  LOAD_BUTTON = "LOAD_BUTTON",
  IDLE = "IDLE",
}

export default function FolderPicker(props: Props) {
  const { provider, isOpen, onClose, onSelect, multi } = props;
  const { t } = useTranslation("components");
  const { enqueueSnackbar } = useSnackbar();
  const [pickerState, setPickerState] = useState<PickerState>(initPickerState);
  const [reqParams, setReqParams] = useState<RequestParams>(initReqParams);
  const { folder, children, selectedFolders, loader } = pickerState;
  const isLoadMore = children.length >= 10;

  const updatePickerState = (update: Partial<PickerState>) =>
    setPickerState(prev => ({
      ...prev,
      ...update,
    }));

  const updateReqParams = (update: Partial<RequestParams>) =>
    setReqParams(prev => ({
      ...prev,
      ...update,
    }));

  useEffect(() => {
    const initPicker = async () => {
      updatePickerState({ loader: LoadingElement.LIST });
      try {
        const storageAccount = await openAuthPopup(
          provider,
          undefined,
          "file_browser"
        );
        const params = { ...reqParams, provider, user: storageAccount.user };
        const [folder, children] = await fetchPickerContent(params);
        updateReqParams(params);
        updatePickerState({ storageAccount, folder, children });
      } catch (e) {
        if (!isClosedByUser(e)) {
          enqueueSnackbar(t("folderPicker.authError"), {
            variant: "error",
          });
        }
        onClose();
      } finally {
        updatePickerState({ loader: LoadingElement.IDLE });
      }
    };

    if (isOpen) initPicker();
  }, [isOpen]);

  const handleNavigation = async (folderId: string) => {
    updatePickerState({ loader: LoadingElement.LIST });
    try {
      const params = { ...reqParams, folderId, limit: 10, offset: 0 };
      const [folder, children] = await fetchPickerContent(params);
      updateReqParams({ folderId });
      updatePickerState({ folder, children });
      if (multi) {
        updatePickerState({
          selectedFolders: selectedFolders.filter(f => f.id !== folderId),
        });
      }
    } catch (e) {
      enqueueSnackbar(t("folderPicker.fetchError"), {
        variant: "error",
      });
    } finally {
      updatePickerState({ loader: LoadingElement.IDLE });
    }
  };

  const handleSort = async (sortConfig: SortConfiguration) => {
    const params = {
      ...reqParams,
      ...sortConfig,
      offset: 0,
    };
    updateReqParams(params);
    updatePickerState({ loader: LoadingElement.LIST });
    try {
      const children = await fetchFolderChildren(params);
      updatePickerState({ children });
    } catch (e) {
      enqueueSnackbar(t("folderPicker.fetchError"), {
        variant: "error",
      });
    } finally {
      updatePickerState({ loader: LoadingElement.IDLE });
    }
  };

  const handleLoadMore = async () => {
    const params = {
      ...reqParams,
      offset: children.length,
    };
    updatePickerState({ loader: LoadingElement.LOAD_BUTTON });
    try {
      const newChildren = await fetchFolderChildren(params);
      updatePickerState({ children: [...children, ...newChildren] });
      updateReqParams(params);
    } catch (e) {
      enqueueSnackbar(t("folderPicker.fetchError"), {
        variant: "error",
      });
    } finally {
      updatePickerState({ loader: LoadingElement.IDLE });
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={isOpen} onClose={onClose}>
      <DialogTitle disableTypography>
        <Typography variant="h6">{t("folderPicker.title")}</Typography>
        <CloseButton size="small" onClick={onClose}>
          <CloseIcon />
        </CloseButton>
        <NavigationContainer>
          <BreadcrumbsLoader loading={loader}>
            <Breadcrumbs currentFolder={folder} onNavigate={handleNavigation} />
          </BreadcrumbsLoader>
          <SortMenu onChange={handleSort} requestParams={reqParams} />
        </NavigationContainer>
      </DialogTitle>
      <ContentLoader loading={loader} rows={children.length}>
        {!children.length && (
          <>
            <Divider />
            <EmptyFolderContainer>
              <FolderOpenIcon color="disabled" />
              <Typography>{t("folderPicker.empty")}</Typography>
              {/* <Link color="primary">{t("folderPicker.backToPrevious")}</Link> */}
            </EmptyFolderContainer>
            <Divider />
          </>
        )}
        {children.length > 0 && (
          <FolderList
            folders={children}
            onItemClick={handleNavigation}
            onSelect={f => {
              if (multi) {
                updatePickerState({ selectedFolders: f });
              } else {
                onSelect(f);
              }
            }}
            multiSelect={multi || false}
            selectedFolders={selectedFolders}
          />
        )}
      </ContentLoader>
      <StyledDialogActions>
        {multi && (
          <Button
            wording={t("folderPicker.buttons.useSelected")}
            onClick={() => onSelect(selectedFolders)}
            disabled={isButtonDisabled(selectedFolders, loader)}
          />
        )}
        {isLoadMore && (
          <ButtonWithLoading
            variant="text"
            loading={pickerState.loader === LoadingElement.LOAD_BUTTON}
            disabled={isButtonDisabled(children, loader)}
            onClick={() => handleLoadMore()}
          >
            {t("folderPicker.buttons.loadMore")}
          </ButtonWithLoading>
        )}
      </StyledDialogActions>
    </Dialog>
  );
}

const isButtonDisabled = (folders: StorageFolder[], loader: LoadingElement) => {
  return !folders.length || loader === LoadingElement.LIST;
};

const initPickerState = (): PickerState => ({
  loader: LoadingElement.IDLE,
  storageAccount: null,
  folder: null,
  children: [],
  selectedFolders: [],
});

const initReqParams = (): RequestParams => ({
  folderId: "root",
  limit: 10,
  offset: 0,
  sortField: "DATE" as SortConfiguration["sortField"],
  sortOrder: "DESC" as SortConfiguration["sortOrder"],
});
